import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Breadcrumb, Col, Modal, Row, Space, Spin, Typography } from 'antd'
import { isEmpty, isNil, map, split, toNumber } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import { HistoryLink } from '../../../common/components/sections'
import { StyledPageSubTitle } from '../../../common/components/styled'
import { APP_ROUTES, EVENT_METADATA_TYPES } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useDocumentTitle, useIngredientsQuery } from '../../../common/hooks'

import { INGREDIENT_SPECIAL_CASE_TYPES } from '../../constants'
import { renderIngredientSpecialCaseEvent } from '../../helpers'
import {
  useCreateIngredientSpecialCaseMutation,
  useIngredientSpecialCaseQuery,
  useUpdateIngredientSpecialCaseMutation,
} from '../../hooks'
import { IngredientSpecialCaseEditorForm } from '../forms'

const IngredientSpecialCaseEditorPage = () => {
  const { id: ingredientSpecialCaseId } = useParams()

  const documentTitle = ingredientSpecialCaseId
    ? 'Edit Ingredient Special Case'
    : 'Create Ingredient Special Case'

  useDocumentTitle(documentTitle)
  const history = useHistory()

  const [
    fetchIngredientSpecialCaseDetails,
    {
      data: ingredientSpecialCase,
      isLoading: isLoadingIngredientSpecialCase,
      error: ingredientSpecialCaseError,
    },
  ] = useIngredientSpecialCaseQuery(true)

  const variables = useMemo(() => ({ disableNodes: () => false }), [])

  const {
    groupedById: ingredientsGroupedById,
    data: ingredientTreeData,
    isLoading: isLoadingIngredients,
    error: ingredientsError,
  } = useIngredientsQuery(variables)

  const [createIngredientSpecialCase] = useCreateIngredientSpecialCaseMutation()
  const [updateIngredientSpecialCase] = useUpdateIngredientSpecialCaseMutation()

  const [showModal, setShowModal] = useState(false)
  const [modalContent, setModalContent] = useState(null)

  const renderEvent = useCallback(
    event =>
      renderIngredientSpecialCaseEvent(event, ingredientsGroupedById, text => {
        setShowModal(true)
        setModalContent(text)
      }),
    [ingredientsGroupedById],
  )

  const handleSubmit = useCallback(
    ingredientSpecialCaseData => {
      let data = { ...ingredientSpecialCaseData }

      if (
        ingredientSpecialCaseData.type ===
        INGREDIENT_SPECIAL_CASE_TYPES.IGNORED_COMBINATION
      ) {
        data = {
          ...data,
          caseDefinition: {
            ingredients_to_check: map(
              ingredientSpecialCaseData.caseDefinition.ingredients_to_check,
              toNumber,
            ),

            tokens: split(ingredientSpecialCaseData.caseDefinition.tokens, ','),
            exclude_pair: false,
            shift_indexes: [0],
            target_ingredient: null,
          },
        }
      }

      if (ingredientSpecialCaseId) {
        updateIngredientSpecialCase(ingredientSpecialCaseId, data)
      } else {
        createIngredientSpecialCase(data)
      }
    },
    [
      createIngredientSpecialCase,
      ingredientSpecialCaseId,
      updateIngredientSpecialCase,
    ],
  )

  useEffect(() => {
    if (isNil(ingredientSpecialCaseId) && !isNil(ingredientSpecialCase?.id)) {
      history.push(
        `${APP_ROUTES.INGREDIENTS.subMenuItems.INGREDIENT_SPECIAL_CASES.link}/${ingredientSpecialCase?.id}`,
      )
    }
  }, [history, ingredientSpecialCase?.id, ingredientSpecialCaseId])

  useEffect(() => {
    if (!isNil(ingredientSpecialCaseId)) {
      fetchIngredientSpecialCaseDetails(ingredientSpecialCaseId)
    }
  }, [fetchIngredientSpecialCaseDetails, ingredientSpecialCaseId])

  useEffect(() => {
    if (ingredientSpecialCaseError || ingredientsError) {
      showErrorNotification({
        title: 'Ingredient Special Case error',
        description: ingredientSpecialCaseError || ingredientsError,
      })
    }
  }, [ingredientSpecialCaseError, ingredientsError])

  if (
    (!isNil(ingredientSpecialCaseId) && isEmpty(ingredientSpecialCase)) ||
    isLoadingIngredientSpecialCase ||
    isLoadingIngredients ||
    isEmpty(ingredientTreeData)
  ) {
    return <Spin />
  }

  return (
    <>
      <StyledPageSubTitle>
        <Row justify="space-between">
          <Col span={12}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to={APP_ROUTES.INGREDIENTS.link}>
                  {APP_ROUTES.INGREDIENTS.name}
                </Link>
              </Breadcrumb.Item>

              <Breadcrumb.Item>
                <Link
                  to={
                    APP_ROUTES.INGREDIENTS.subMenuItems.INGREDIENT_SPECIAL_CASES
                      .link
                  }
                >
                  {
                    APP_ROUTES.INGREDIENTS.subMenuItems.INGREDIENT_SPECIAL_CASES
                      .name
                  }
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{ingredientSpecialCase?.name}</Breadcrumb.Item>
            </Breadcrumb>
          </Col>

          {ingredientSpecialCase && (
            <Space>
              <UpdateDataText
                updatedAt={ingredientSpecialCase.updatedAt}
                updatedBy={ingredientSpecialCase.updatedBy.name}
              />

              <HistoryLink
                entityId={ingredientSpecialCase.id}
                filterEventTypes
                eventMetadataTypes={[
                  EVENT_METADATA_TYPES.INGREDIENT_SPECIAL_CASE,
                ]}
                renderEvent={renderEvent}
              />
            </Space>
          )}
        </Row>
      </StyledPageSubTitle>
      <IngredientSpecialCaseEditorForm
        ingredientSpecialCase={ingredientSpecialCase}
        onSubmit={handleSubmit}
        ingredientTreeData={ingredientTreeData}
      />

      <Modal
        title="Event Details"
        visible={showModal}
        onCancel={() => {
          setShowModal(false)
          setModalContent(null)
        }}
        footer={null}
      >
        <Typography.Text>{modalContent}</Typography.Text>
      </Modal>
    </>
  )
}

export default IngredientSpecialCaseEditorPage
